import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { useRealtime } from '../../lib/realtime';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { ChefHat, Wine, Clock, Check, CheckCheck } from 'lucide-react';

const minutesSince = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 60000);

/**
 * Reusable Kitchen/Bar Display board. Updates in real-time via SSE and keeps a
 * slow poll as a fallback. Renders tickets with large, touch-friendly controls.
 */
export const KdsStation = ({ station = 'cucina', emptyLabel }) => {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [, setTick] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get('/kds', { params: station ? { station } : {} });
      setTickets(data);
    } catch (e) { /* keep last data on transient errors */ }
  };

  useRealtime((msg) => {
    if (msg && (msg.event === 'kds' || msg.event === 'orders')) load();
  });

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    const c = setInterval(() => setTick((x) => x + 1), 30000);
    return () => { clearInterval(t); clearInterval(c); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [station]);

  const setStatus = async (orderId, itemId, status) => {
    await api.patch(`/orders/${orderId}/items/${itemId}/kitchen-status`, { status });
    load();
  };

  const EmptyIcon = station === 'bar' ? Wine : ChefHat;

  if (tickets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-muted-foreground" data-testid="kds-empty">
        <EmptyIcon className="h-14 w-14 mb-3 opacity-40" />
        <div className="text-lg">{emptyLabel || (station === 'bar' ? t('kds.emptyBar') : t('kds.emptyKitchen'))}</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="kds-board">
      {tickets.map((ticket) => {
        const mins = minutesSince(ticket.created_at);
        const overdue = mins >= 12;
        const warn = mins >= 8 && mins < 12;
        return (
          <Card key={ticket.order_id} data-testid="kds-ticket" className={cn('p-0 overflow-hidden border-2', overdue ? 'border-kds-overdue' : warn ? 'border-kds-preparing' : 'border-border')}>
            <div className={cn('flex items-center justify-between px-4 py-3', overdue ? 'bg-kds-overdue/10' : 'bg-muted')}>
              <div className="font-display font-semibold text-lg">{ticket.table_name}</div>
              <div className={cn('flex items-center gap-1.5 text-base font-medium tabular', overdue ? 'text-kds-overdue' : warn ? 'text-kds-preparing' : 'text-muted-foreground')}>
                <Clock className="h-5 w-5" /> {mins}'
              </div>
            </div>
            <div className="p-3 space-y-2.5">
              {ticket.items.map((it) => (
                <div key={it.id} className={cn('rounded-xl border p-3', it.kitchen_status === 'ready' ? 'border-kds-ready bg-kds-ready/5' : 'border-border')}>
                  <div className="min-w-0">
                    <div className="font-medium text-base leading-snug"><span className="text-primary font-display">{it.qty}x</span> {it.name}{it.variant ? ` (${it.variant})` : ''}</div>
                    {it.extras?.length > 0 && <div className="text-sm text-muted-foreground">+ {it.extras.map((e) => e.name).join(', ')}</div>}
                    {it.notes && <div className="text-sm text-accent font-medium">{it.notes}</div>}
                  </div>
                  <div className="mt-2.5">
                    {it.kitchen_status === 'preparing' ? (
                      <Button onClick={() => setStatus(ticket.order_id, it.id, 'ready')} className="w-full h-12 rounded-xl text-base" data-testid="kds-ticket-ready-button" style={{ backgroundColor: 'hsl(var(--kds-ready))' }}>
                        <Check className="h-5 w-5 mr-1.5" /> {t('kds.ready')}
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={() => setStatus(ticket.order_id, it.id, 'delivered')} className="w-full h-12 rounded-xl text-base" data-testid="kds-ticket-delivered-button">
                        <CheckCheck className="h-5 w-5 mr-1.5" /> {t('kds.delivered')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
};
